/**
 * Dias de atendimento do colaborador.
 *
 * Os valores são os nomes do DayOfWeek do Java, que é o formato que o backend grava e
 * devolve. O rótulo curto é o que aparece nos botões do formulário, que precisam caber
 * lado a lado também no celular.
 */
export const WEEK_DAYS = [
    { value: 'MONDAY', label: 'Seg' },
    { value: 'TUESDAY', label: 'Ter' },
    { value: 'WEDNESDAY', label: 'Qua' },
    { value: 'THURSDAY', label: 'Qui' },
    { value: 'FRIDAY', label: 'Sex' },
    { value: 'SATURDAY', label: 'Sáb' },
    { value: 'SUNDAY', label: 'Dom' }
];

export const ALL_WORK_DAYS = WEEK_DAYS.map(day => day.value);

/**
 * Ordena os dias pela semana, independentemente da ordem em que foram marcados.
 *
 * O backend já devolve ordenado, mas o array do formulário cresce na ordem dos cliques:
 * sem isso, marcar sábado antes de segunda mandaria a lista fora de ordem.
 */
export const sortWorkDays = (days) => {
    if (!days) {
        return [];
    }

    return ALL_WORK_DAYS.filter(day => days.includes(day));
};

// Date.getDay() devolve 0 para domingo; o backend usa os nomes do DayOfWeek do Java.
const JS_DAY_TO_WORK_DAY = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

/** Converte uma data do JS para o nome do dia usado pelo backend. */
export const toWorkDay = (date) => JS_DAY_TO_WORK_DAY[date.getDay()];

/** Interpreta datas do formulário no fuso local, sem deslocar o dia por UTC. */
export const toWorkDayFromInput = (value) => {
    if (typeof value === 'string') {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return toWorkDay(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
        }
    }

    return value instanceof Date ? toWorkDay(value) : null;
};

/**
 * Se o colaborador atende na data informada.
 *
 * Lista de dias vazia responde true: os colaboradores cadastrados antes deste campo
 * existir não têm nada gravado, e a agenda os trata como atendendo a semana toda.
 */
export const worksOnDate = (workDays, date) => {
    if (!date || !workDays || workDays.length === 0) {
        return true;
    }

    return workDays.includes(toWorkDay(date));
};

/** Rótulo do dia da semana para as mensagens da agenda. */
export const workDayLabel = (date) => {
    const workDay = toWorkDay(date);
    const found = WEEK_DAYS.find(day => day.value === workDay);

    return found ? found.label : '';
};

/**
 * Grade de horário por dia da semana, no formato que o backend grava
 * (EmployeeWorkingHours): um item por dia, com atendimento, entrada, saída e intervalo.
 *
 * Sempre com os sete dias, mesmo os de folga: o backend substitui a grade inteira a cada
 * gravação, e omitir um dia significaria apagá-lo em vez de marcá-lo como folga.
 */
export const buildWorkingHours = (existing, workDays) => {
    const byDay = {};
    (existing || []).forEach(item => {
        if (item && item.dayOfWeek) {
            byDay[item.dayOfWeek] = item;
        }
    });

    // Cadastro antigo: a grade ainda não existe, então os dias vêm do campo de dias e o
    // horário fica em branco, o que significa "acompanha o expediente da filial".
    const legacyDays = (workDays && workDays.length > 0) ? workDays : ALL_WORK_DAYS;

    return ALL_WORK_DAYS.map(day => {
        const found = byDay[day];

        if (found) {
            return {
                dayOfWeek: day,
                isWorking: Boolean(found.isWorking),
                startTime: found.startTime || '',
                endTime: found.endTime || '',
                breakStartTime: found.breakStartTime || '',
                breakEndTime: found.breakEndTime || ''
            };
        }

        return {
            dayOfWeek: day,
            isWorking: legacyDays.includes(day),
            startTime: '',
            endTime: '',
            breakStartTime: '',
            breakEndTime: ''
        };
    });
};

/** Os dias marcados como de atendimento, para o campo antigo e para as validações. */
export const workingDaysOf = (workingHours) =>
    (workingHours || []).filter(item => item && item.isWorking).map(item => item.dayOfWeek);

/** Converte "" em null antes de enviar: string vazia não é um horário. */
export const toWorkingHoursPayload = (workingHours) =>
    (workingHours || []).map(item => ({
        dayOfWeek: item.dayOfWeek,
        isWorking: Boolean(item.isWorking),
        startTime: item.startTime || null,
        endTime: item.endTime || null,
        breakStartTime: item.breakStartTime || null,
        breakEndTime: item.breakEndTime || null
    }));

/** Nome por extenso do dia, para os rótulos da grade de horário. */
export const WEEK_DAY_LONG_LABELS = {
    MONDAY: 'Segunda-feira',
    TUESDAY: 'Terça-feira',
    WEDNESDAY: 'Quarta-feira',
    THURSDAY: 'Quinta-feira',
    FRIDAY: 'Sexta-feira',
    SATURDAY: 'Sábado',
    SUNDAY: 'Domingo'
};
